<template>
    <section class="images-manager pb-5">

        <div class="row mb-4">
            <div class="col-12">
                <b-card no-body class="shadow-sm border-0">
                    <div class="card-body">
                        <div class="upload-drop-zone p-5 text-center rounded transition-all" :class="{'is-dragover': $refs.upload && $refs.upload.dropActive}">

                            <div v-if="!files.length" class="empty-state">
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" class="text-primary mb-3" viewBox="0 0 16 16">
                                    <path fill-rule="evenodd" d="M7.646 5.146a.5.5 0 0 1 .708 0l2 2a.5.5 0 0 1-.708.708L8.5 6.707V10.5a.5.5 0 0 1-1 0V6.707L6.354 7.854a.5.5 0 1 1-.708-.708l2-2z"/>
                                    <path d="M4.406 3.342A5.53 5.53 0 0 1 8 2c2.69 0 4.923 2 5.166 4.579C14.758 6.804 16 8.137 16 9.773 16 11.569 14.502 13 12.687 13H3.781C1.708 13 0 11.366 0 9.318c0-1.763 1.266-3.223 2.942-3.593.143-.863.698-1.723 1.464-2.383zm.653.757c-.757.653-1.153 1.44-1.153 2.056v.448l-.445.049C2.064 6.805 1 7.952 1 9.318 1 10.785 2.23 12 3.781 12h8.906C13.98 12 15 10.988 15 9.773c0-1.216-1.02-2.228-2.313-2.228h-.5v-.5C12.188 4.825 10.328 3 8 3a4.53 4.53 0 0 0-2.941 1.1z"/>
                                </svg>
                                <h4>{{ $t('forms.dropHere') }}</h4>
                                <p class="text-muted mb-4">{{ $t('forms.or') }}</p>

                                <file-upload
                                    class="btn btn-primary px-4 py-2 font-weight-bold"
                                    :post-action="uploadUrl" :multiple="true" :headers="authHeader"
                                    @input-file="inputFile" @input-filter="inputFilter"
                                    :drop="true" v-model="files" ref="upload">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" fill="currentColor" class="mr-1" viewBox="0 0 16 16">
                                        <path d="M1 3.5A1.5 1.5 0 0 1 2.5 2h2.764c.958 0 1.76.56 2.311 1.184C7.985 3.648 8.48 4 9 4h4.5A1.5 1.5 0 0 1 15 5.5v.64c.57.265.94.876.856 1.546l-.64 5.124A2.5 2.5 0 0 1 12.733 15H3.266a2.5 2.5 0 0 1-2.481-2.19l-.64-5.124A1.5 1.5 0 0 1 1 6.14V3.5zM2 6h12v-.5a.5.5 0 0 0-.5-.5H9c-.964 0-1.71-.629-2.174-1.154C6.374 3.334 5.82 3 5.264 3H2.5a.5.5 0 0 0-.5.5V6zm-.367 1a.5.5 0 0 0-.496.562l.64 5.124A1.5 1.5 0 0 0 3.266 14h9.468a1.5 1.5 0 0 0 1.489-1.314l.64-5.124A.5.5 0 0 0 14.367 7H1.633z"/>
                                    </svg>
                                    {{ $t('forms.selectFromDisk') }}
                                </file-upload>
                            </div>

                            <div v-else class="files-queue text-left">
                                <h5 class="mb-3 border-bottom pb-2">{{ $t('forms.prepareToUpload') }} ({{ files.length }})</h5>
                                <b-table responsive hover :items="files" :fields="fields" small class="bg-white border rounded">
                                    <template v-slot:cell(file.name)="data">
                                        <strong>{{ data.item.name }}</strong>
                                        <div class="small text-muted" v-if="data.item.error">{{ data.item.error }}</div>
                                    </template>
                                    <template v-slot:cell(file.size)="data">
                                        {{ humanFileSize(data.item.size) }}
                                    </template>
                                    <template v-slot:cell(file)="data">
                                        <b-badge v-if="data.item.error" variant="danger">{{ $t('forms.statusError') }}</b-badge>
                                        <b-badge v-else-if="data.item.success" variant="success">{{ $t('forms.statusOk') }}</b-badge>
                                        <b-badge v-else-if="data.item.active" variant="warning" class="text-dark">{{ $t('forms.statusSending') }}</b-badge>
                                        <b-badge v-else variant="secondary">{{ $t('forms.statusWaiting') }}</b-badge>
                                    </template>
                                </b-table>

                                <div class="d-flex justify-content-between mt-3">
                                    <b-button variant="outline-danger" size="sm" @click="clearFiles">{{ $t('forms.cleanList') }}</b-button>

                                    <div>
                                        <file-upload class="btn btn-outline-primary btn-sm mr-2"
                                                     :post-action="uploadUrl" :multiple="true" :headers="authHeader"
                                                     @input-file="inputFile" @input-filter="inputFilter"
                                                     :drop="true" v-model="files" ref="upload">
                                            {{ $t('forms.addNext') }}
                                        </file-upload>

                                        <b-button variant="success" size="sm" @click.prevent="startUpload" v-if="!isUploading">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" fill="currentColor" class="mr-1" viewBox="0 0 16 16">
                                                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                                                <path d="M6.271 5.055a.5.5 0 0 1 .52.038l3.5 2.5a.5.5 0 0 1 0 .814l-3.5 2.5A.5.5 0 0 1 6 10.5v-5a.5.5 0 0 1 .271-.445z"/>
                                            </svg>
                                            {{ $t('forms.startUpload') }}
                                        </b-button>

                                        <b-button variant="danger" size="sm" @click.prevent="stopUpload" v-else>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" fill="currentColor" class="mr-1" viewBox="0 0 16 16">
                                                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                                                <path d="M5 6.5A1.5 1.5 0 0 1 6.5 5h3A1.5 1.5 0 0 1 11 6.5v3A1.5 1.5 0 0 1 9.5 11h-3A1.5 1.5 0 0 1 5 9.5v-3z"/>
                                            </svg>
                                            {{ $t('forms.stopUpload') }}
                                        </b-button>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </b-card>
            </div>
        </div>

        <div class="row">
            <div class="col-12">
                <b-card no-body class="shadow-sm border-0">
                    <div class="card-header bg-white border-bottom d-flex justify-content-between align-items-center py-3">
                        <h5 class="m-0 font-weight-bold text-secondary">{{ $t('forms.imageGallery') }}</h5>
                        <b-input-group size="sm" style="max-width: 300px;">
                            <b-form-input v-model="searchQuery" type="search" :placeholder="$t('forms.search')"></b-form-input>
                            <b-input-group-append><b-button disabled variant="outline-secondary"><span aria-hidden="true">🔍</span></b-button></b-input-group-append>
                        </b-input-group>
                    </div>

                    <div class="card-body bg-light">
                        <div v-if="paginatedItems.length > 0" class="image-grid">
                            <div class="image-tile border rounded bg-white shadow-sm"
                                 v-for="image in paginatedItems" :key="image.id"
                                 :id="'image-wrapper-' + image.id"
                                 @click="openEditModal(image.id)">

                                <div class="image-thumbnail d-flex justify-content-center align-items-center p-2">
                                    <img :src="mediaHostname + image.filename" :alt="image.alt" class="img-fluid" style="max-height: 120px; object-fit: contain;">
                                </div>

                                <div class="image-footer bg-light border-top text-truncate px-2 py-1 small text-center" :title="image.filename.substr(8)">
                                    {{ image.filename.substr(8) }}
                                </div>

                                <b-popover :target="'image-wrapper-' + image.id" triggers="hover" placement="top">
                                    <div class="text-center">
                                        <strong class="d-block">{{image.width}} x {{image.height}} px</strong>
                                        <span class="text-muted small">{{image.filename.substr(8)}}</span>
                                    </div>
                                </b-popover>
                            </div>
                        </div>
                        <div v-else class="text-center py-5 text-muted">
                            <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" fill="currentColor" class="mr-1" viewBox="0 0 16 16">
                                <path d="M6.002 5.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"/>
                                <path d="M2.002 1a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2h-12zm12 1a1 1 0 0 1 1 1v6.5l-3.777-1.947a.5.5 0 0 0-.577.093l-3.71 3.71-2.66-1.772a.5.5 0 0 0-.63.062L1.002 12V3a1 1 0 0 1 1-1h12z"/>
                            </svg>
                            <h5>{{ $t('forms.fileNotFound') }}</h5>
                            <p>{{ $t('forms.fileNotFoundDesc') }}</p>
                        </div>
                    </div>

                    <div class="card-footer bg-white d-flex justify-content-center pt-3" v-if="filteredItems.length > perPage">
                        <b-pagination v-model="currentPage" :total-rows="filteredItems.length" :per-page="perPage"></b-pagination>
                    </div>
                </b-card>
            </div>
        </div>

        <b-modal id="edit-img-modal" ref="modal" :title="$t('forms.edit')" header-bg-variant="primary" header-text-variant="light" :cancelTitle="$t('forms.cancel')" :okTitle="$t('forms.save')" @ok="editImage" size="lg">
            <div class="row">
                <div class="col-lg-5 text-center bg-light rounded p-3 d-flex align-items-center justify-content-center border">
                    <img :src="mediaHostname + editedItem.filename" class="img-fluid" style="max-height: 250px; object-fit: contain;">
                </div>
                <div class="col-lg-7">
                    <b-form-group :label="$t('forms.name')" label-for="edit-name-input">
                        <b-input-group>
                            <b-form-input :value="formatFilename" type="text" id="edit-name-input" disabled/>
                            <b-input-group-append>
                                <b-button variant="danger" @click="deleteImage" :title="$t('forms.delete')">&times;</b-button>
                            </b-input-group-append>
                        </b-input-group>
                    </b-form-group>

                    <b-form-group :label="$t('forms.imageSize')" label-for="edit-size-input">
                        <b-form-input :value="formatSize" type="text" id="edit-size-input" disabled/>
                    </b-form-group>

                    <b-form-group :label="$t('forms.imageAlt')" label-for="edit-alt-input">
                        <b-form-input v-model="editedItem.alt" type="text" id="edit-alt-input" :placeholder="$t('forms.imageAlt')"/>
                    </b-form-group>
                </div>
            </div>
        </b-modal>

    </section>
</template>


<script>
import axios from "axios";
import {Const} from "../../config";
import jwtService from "../../mixins/JwtStorageService";

export default {
    name: "ImageList",
    data() {
        return {
            mediaHostname: Const.MEDIA_HOST,
            items: [],
            editedItem: {},
            files: [],
            isUploading: false,
            uploadUrl: Const.API_HOST + '/admin/image/upload',
            authHeader: {},
            fields: [
                { key: 'file.name', label: this.$t('forms.name') },
                { key: 'file.size', label: this.$t('forms.size') },
                { key: 'file', label: this.$t('forms.status'), thStyle: 'width: 120px;' }
            ],
            searchQuery: '',
            currentPage: 1,
            perPage: 40,
        }
    },
    computed: {
        formatFilename() {
            if (this.editedItem && this.editedItem.filename)
                return this.editedItem.filename.substr(8);
            return '';
        },
        formatSize() {
            if (this.editedItem && this.editedItem.width && this.editedItem.height)
                return this.editedItem.width + ' x ' + this.editedItem.height;
            return '';
        },
        filteredItems() {
            if (!this.searchQuery) return this.items || [];
            let q = this.searchQuery.toLowerCase();
            return this.items.filter(img => img.filename && img.filename.toLowerCase().includes(q));
        },
        paginatedItems() {
            const start = (this.currentPage - 1) * this.perPage;
            return this.filteredItems.slice(start, start + this.perPage);
        }
    },
    watch: {
        searchQuery() {
            this.currentPage = 1;
        }
    },
    mounted() {
        this.fetchImages();

        const token = jwtService.getAccessToken();
        if (token) {
            this.authHeader = {'Authorization': 'Bearer ' + token}
        }
    },
    methods: {
        fetchImages() {
            axios.get(Const.API_HOST + '/admin/images')
                .then(response => { this.items = response.data; })
                // eslint-disable-next-line no-console
                .catch(error => console.error(error));
        },
        openEditModal(id) {
            this.editedItem = JSON.parse(JSON.stringify(this.items.find(obj => obj.id === id)));
            this.$bvModal.show('edit-img-modal');
        },
        editImage() {
            axios.post(Const.API_HOST + '/admin/image/edit', {
                id: this.editedItem.id,
                alt: this.editedItem.alt
            }).then(() => {
                this.fetchImages();
                // eslint-disable-next-line no-console
            }).catch(err => console.error(err));
            this.editedItem = {};
        },
        deleteImage() {
            if(!confirm(this.$t('forms.deleteImageConfirmation'))) return;
            axios.post(Const.API_HOST + '/admin/image/remove', {
                id: this.editedItem.id
            }).then(() => {
                this.fetchImages();
                // eslint-disable-next-line no-console
            }).catch(err => console.error(err));
            this.editedItem = {};
            this.$bvModal.hide('edit-img-modal');
        },
        inputFile: function (newFile, oldFile) {
            if (newFile && oldFile && newFile.success && !oldFile.success) {
                this.fetchImages();
            }

            if (this.$refs.upload && !this.$refs.upload.active) {
                this.isUploading = false;
            }
        },
        inputFilter: function (newFile, oldFile, prevent) {
            if (newFile && !oldFile) {
                if (!/\.(jpeg|jpe|jpg|gif|png|webp)$/i.test(newFile.name)) {
                    return prevent();
                }
            }
            newFile.blob = '';
            let URL = window.URL || window.webkitURL;
            if (URL && URL.createObjectURL) {
                newFile.blob = URL.createObjectURL(newFile.file);
            }
        },
        humanFileSize(size) {
            if (!size) return '0 B';
            var i = Math.floor(Math.log(size) / Math.log(1024));
            return (size / Math.pow(1024, i)).toFixed(2) * 1 + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
        },
        startUpload() {
            if (this.$refs.upload) {
                this.$refs.upload.active = true;
                this.isUploading = true;
            }
        },
        stopUpload() {
            if (this.$refs.upload) {
                this.$refs.upload.active = false;
                this.isUploading = false;
            }
        },
        clearFiles() {
            this.files = [];
            this.isUploading = false;
            if (this.$refs.upload) {
                this.$refs.upload.clear();
            }
        },
    }
}
</script>


<style scoped>
.upload-drop-zone {
    border: 2px dashed #ced4da;
    background-color: #f8f9fa;
    transition: all 0.3s ease-in-out;
}

.upload-drop-zone.is-dragover {
    border-color: #007bff;
    background-color: #e9ecef;
    box-shadow: 0 0 10px rgba(0, 123, 255, 0.2);
}

.image-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 15px;
}

.image-tile {
    cursor: pointer;
    overflow: hidden;
    transition: transform 0.2s, box-shadow 0.2s;
    height: 160px;
    display: flex;
    flex-direction: column;
}

.image-tile:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 15px rgba(0,0,0,0.1) !important;
    border-color: #007bff !important;
}

.image-thumbnail {
    flex-grow: 1;
    overflow: hidden;
    background-color: #fff;
}

::v-deep .file-uploads label {
    margin-bottom: 0;
    cursor: pointer;
}
</style>
